import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createAdsTxtClient } from './api';
import type {
  AdsTxtAppsFilter,
  AdsTxtDomainsFilter,
  AdsTxtRecordPayload,
  AdsTxtRecordsFilter,
} from './model';

const adsTxtQueryKey = ['supply', 'ads-txt'] as const;
export const adsTxtRecordsListsQueryKey = [...adsTxtQueryKey, 'records', 'lists'] as const;
export const adsTxtAppsListsQueryKey = [...adsTxtQueryKey, 'apps', 'lists'] as const;
export const adsTxtDomainsListsQueryKey = [...adsTxtQueryKey, 'domains', 'lists'] as const;
export const adsTxtDomainsCountsQueryKey = [...adsTxtQueryKey, 'domains', 'counts'] as const;
const adsTxtLookupsQueryKey = [...adsTxtQueryKey, 'lookups'] as const;

function useAdsTxtClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return useMemo(
    () =>
      createAdsTxtClient({
        config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
        accessToken: session.accessToken,
      }),
    [config.apiBaseUrl, config.networkId, session.accessToken],
  );
}

export function useAdsTxtRecordsQuery(filter: AdsTxtRecordsFilter) {
  const client = useAdsTxtClient();
  return useQuery({
    queryKey: [...adsTxtRecordsListsQueryKey, filter],
    queryFn: () => client.listRecords(filter),
  });
}

export function useAdsTxtAppsQuery(filter: AdsTxtAppsFilter) {
  const client = useAdsTxtClient();
  return useQuery({
    queryKey: [...adsTxtAppsListsQueryKey, filter],
    queryFn: () => client.listApps(filter),
  });
}

export function useAdsTxtDomainsQuery(filter: AdsTxtDomainsFilter) {
  const client = useAdsTxtClient();
  return useQuery({
    queryKey: [...adsTxtDomainsListsQueryKey, filter],
    queryFn: () => client.listDomains(filter),
  });
}

export function useAdsTxtDomainCountsQuery() {
  const client = useAdsTxtClient();
  return useQuery({
    queryKey: adsTxtDomainsCountsQueryKey,
    queryFn: client.countDomains,
  });
}

export function useAdsTxtPublishersQuery() {
  const client = useAdsTxtClient();
  return useQuery({
    queryKey: [...adsTxtLookupsQueryKey, 'publishers'],
    queryFn: client.listPublishers,
    staleTime: 60_000,
  });
}

export function useAdsTxtDemandPartnersQuery() {
  const client = useAdsTxtClient();
  return useQuery({
    queryKey: [...adsTxtLookupsQueryKey, 'demand-partners'],
    queryFn: client.listDemandPartners,
    staleTime: 60_000,
  });
}

export function useAdsTxtMutations() {
  const client = useAdsTxtClient();
  const queryClient = useQueryClient();
  const invalidateRecords = () =>
    queryClient.invalidateQueries({ queryKey: adsTxtRecordsListsQueryKey });
  return {
    createRecord: useMutation({ mutationFn: client.createRecord, onSuccess: invalidateRecords }),
    updateRecord: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: AdsTxtRecordPayload }) =>
        client.updateRecord(id, payload),
      onSuccess: invalidateRecords,
    }),
    deleteRecord: useMutation({ mutationFn: client.deleteRecord, onSuccess: invalidateRecords }),
    // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- read-only CSV export, writes no server state
    exportRecords: useMutation({ mutationFn: client.exportRecords }),
    crawlApp: useMutation({
      mutationFn: client.crawlApp,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: adsTxtAppsListsQueryKey }),
    }),
    crawlDomain: useMutation({
      mutationFn: ({ publisherId, domainId }: { publisherId: string; domainId: string }) =>
        client.crawlDomain(publisherId, domainId),
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: adsTxtDomainsListsQueryKey }),
          queryClient.invalidateQueries({ queryKey: adsTxtDomainsCountsQueryKey }),
        ]),
    }),
  };
}
